// @ts-nocheck
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Container, Button, Avatar } from '@mui/material';
import useUserObject from 'app/hooks/useUserObject';
import toast from 'react-hot-toast';

export function ManageSubscriptionPage() {
  const { user, refetchUser } = useUserObject();
  const [userProfileFromContext] = useOutletContext();
  const navigate = useNavigate();
  
  // Use user profile from useUserObject hook for real-time updates
  const userProfile = user?.user_profile || userProfileFromContext;

  // Use manage_subscription object for all subscription-related data
  const manageSubscription = userProfile?.manage_subscription || {};
  
  const email = user?.email || userProfile?.email || 'user@example.com';
  const username = userProfile?.username || email.split('@')[0];
  const swapCredits = manageSubscription?.credits?.inference || 0;
  const catalogCredits = manageSubscription?.credits?.marketplace || 0;
  const voiceCredits = manageSubscription?.credits?.voice_model || 0;
  const plan = manageSubscription?.plan || 'Standard';
  
  // Check plan type and get display text
  const isCreator = plan?.includes('creator');
  const isStarter = plan?.includes('starter');
  let planText = "Free Plan"
  if (isCreator) planText = "Creator Plan"
  if (isStarter) planText = "Starter Plan"
  
  // Get subscription details from manage_subscription
  const subscriptionEndDate = manageSubscription?.subscription_end_date;
  const isCancellation = manageSubscription?.is_cancellation || false;
  const hasActiveSubscription = manageSubscription?.has_active_subscription || false;
  const isActivelyPaused = manageSubscription?.is_actively_paused || false;
  
  // Check if user is on free plan
  const isFreePlan = planText === "Free Plan";
  
  // Determine billing cycle
  const billingCycle = manageSubscription?.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly';
  
  // Get coupons data (array)
  const couponsData = manageSubscription?.coupons || [];
  
  // Get pause data
  const pauseData = manageSubscription?.pause;
  
  // Determine if subscription is paused
  const isPaused = pauseData !== null && pauseData !== undefined;
  const pauseType = pauseData?.type || (pauseData?.behavior ? 'payment_collection' : null);
  const isStatusPaused = pauseType === 'status_paused';
  
  // Format pause resume date if available
  const pauseResumeDate = pauseData?.resumes_at ? new Date(pauseData.resumes_at * 1000).toLocaleDateString() : null;
  
  // Get avatar URL from user object
  const avatarUrl = user?.imageUrl || (user?.identities && user.identities[0]?.identity_data?.avatar_url);

  const handleCancelSubscription = () => {
    // https://docs.churnkey.co/cancel-flows/quick-start-guide#step-three-launch-churnkey
    window.churnkey.init('show', {
      subscriptionId: manageSubscription?.stripe_subscription_id,
      customerId: manageSubscription?.stripe_customer_id,
      authHash: manageSubscription?.churnkey_hash,
      appId: '2l1cnhdje', // required
      mode: process.env.NODE_ENV === 'production' ? 'live' : 'test',  // 'live' / 'test' to hit test billing provider environment
      provider: 'stripe',
      record: true,  // enables session playback recording
      preview: false,
      onPause: () => setTimeout(refetchUser, 100),
      onCancel: () => setTimeout(refetchUser, 100),
      onDiscount: () => setTimeout(refetchUser, 100),
      onGoToAccount: () => setTimeout(refetchUser, 100),
      onClose: () => setTimeout(refetchUser, 100),
    });
  }

  const getPlanBenefits = (plan: string) => {
    const benefits = {
      'Standard': [
        '300 monthly swap credits',
        '100 monthly catalog credits',
        'Access to voice training',
        'Standard quality outputs',
        'Email support'
      ],
      'Premium': [
        '1000 monthly swap credits',
        '500 monthly catalog credits',
        'Priority voice training',
        'High quality outputs',
        'Priority support',
        'Early access to new features'
      ],
      'Enterprise': [
        'Unlimited swap credits',
        'Unlimited catalog credits',
        'Dedicated voice training resources',
        'Maximum quality outputs',
        'Dedicated account manager',
        'Custom integrations',
        'SLA guarantee'
      ]
    };
    return benefits[plan] || benefits['Standard'];
  }

  return (
    <>
      <Helmet>
        <title>Account Management - Controlla Voice</title>
        <meta name="description" content="Manage your subscription and account settings" />
      </Helmet>
      
      <PageContainer>
        <MotionContent
          initial={{ translateY: '50px', opacity: 0 }}
          animate={{ translateY: '0px', opacity: 1 }}
          exit={{ translateY: '0px', opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Container maxWidth="lg" sx={{ '@media (max-width: 600px)': { px: 2 } }}>
            <PageHeader>
              <PageTitle>Account Management</PageTitle>
            </PageHeader>

            <AccountSection>
              <SectionHeader>
                <SectionTitle>User Profile</SectionTitle>
              </SectionHeader>
              <AccountCard>
                <ProfileContainer>
                  <UserDetailsColumn>
                    <ProfileRow>
                      <Avatar 
                        alt={email} 
                        src={avatarUrl}
                        sx={{ width: 64, height: 64, bgcolor: '#6c63ff' }}
                      >
                        {!avatarUrl && username.charAt(0).toUpperCase()}
                      </Avatar>
                      <UserInfo>
                        <UserEmail>{email}</UserEmail>
                        <UserPlan>{planText}</UserPlan>
                      </UserInfo>
                    </ProfileRow>
                  </UserDetailsColumn>
                  <CreditsColumn>
                    <CreditItem color="#55CDC4">
                      <span>{swapCredits} Swap Credits</span>
                    </CreditItem>
                    <CreditItem color="#0b92ff">
                      <span>{catalogCredits} Catalog Credits</span>
                    </CreditItem>
                    <CreditItem color="#FF74E9">
                      <span>{voiceCredits} Voice Credits</span>
                      <BuyCreditsLink onClick={() => navigate('/app/refill')}>
                        Buy credits
                      </BuyCreditsLink>
                    </CreditItem>
                  </CreditsColumn>
                </ProfileContainer>
              </AccountCard>
            </AccountSection>

            <SubscriptionSection>
              <SectionHeader>
                <SectionTitle>Subscription</SectionTitle>
              </SectionHeader>

              <SubscriptionCard>
                <SubscriptionInfoRow isFreePlan={isFreePlan}>
                  <div>
                    <SubscriptionLabel>{isActivelyPaused ? 'Paused Subscription' : 'Active Subscription'}</SubscriptionLabel>
                    <SubscriptionPlan>
                      {planText}
                      {hasActiveSubscription && (
                        <BillingCycle>{billingCycle}</BillingCycle>
                      )}
                    </SubscriptionPlan>
                  </div>
                  {hasActiveSubscription && (
                    <SubscriptionStatus isCancellation={isCancellation || isActivelyPaused}>
                      {isCancellation ? `Ending on ${subscriptionEndDate}` : 
                       isActivelyPaused && pauseResumeDate ? `Paused until ${pauseResumeDate}` :
                       isActivelyPaused ? 'Paused' :
                       `Renews on ${subscriptionEndDate}`}
                    </SubscriptionStatus>
                  )}
                  {!hasActiveSubscription && !isFreePlan && (
                    <SubscriptionStatus isCancellation={true}>
                      Inactive
                    </SubscriptionStatus>
                  )}
                  {isFreePlan && (
                    <ManageButton 
                      variant="contained"
                      onClick={() => navigate('/app/upgrade')}
                    >
                      View Plans
                    </ManageButton>
                  )}
                </SubscriptionInfoRow>
                
                {isPaused && !isCancellation && (
                  <PauseStatusContainer>
                    <PauseStatusInfo>
                      <PauseStatusLabel>Subscription Paused</PauseStatusLabel>
                      <PauseStatusDetails>
                        {pauseType === 'status_paused' && (
                          <PauseStatusText>
                            Your subscription is currently paused. Payment collection has stopped.
                          </PauseStatusText>
                        )}
                        {pauseType === 'payment_collection' && (
                          <PauseStatusText>
                            Payment collection is paused. Your subscription remains active.
                            {pauseResumeDate && ` Billing will resume on ${pauseResumeDate}.`}
                          </PauseStatusText>
                        )}
                        {pauseData?.behavior === 'void' && (
                          <PauseStatusNote>Services are temporarily free during this period.</PauseStatusNote>
                        )}
                        {pauseData?.behavior === 'keep_as_draft' && (
                          <PauseStatusNote>Invoices are being saved as drafts and will be collected later.</PauseStatusNote>
                        )}
                        {pauseData?.behavior === 'mark_uncollectible' && (
                          <PauseStatusNote>Outstanding invoices have been marked as uncollectible.</PauseStatusNote>
                        )}
                      </PauseStatusDetails>
                    </PauseStatusInfo>
                  </PauseStatusContainer>
                )}
                
                {couponsData && couponsData.length > 0 && (
                  <CouponsContainer>
                    <CouponLabel>{ isCancellation ? "Reactivate your subscription to keep this one-time offer" : "Active Discounts" }</CouponLabel>
                    {couponsData.map((couponData, index) => (
                      <CouponInfo key={couponData.id || index}>
                        <CouponDetails>
                          {/* {couponData.name && <CouponName>{couponData.name}</CouponName>} */}
                          <CouponDiscount>
                            {couponData.percent_off ? `${couponData.percent_off}% off` : 
                             couponData.amount_off ? `$${(couponData.amount_off / 100).toFixed(2)} off` : 
                             'Discount applied'}
                          </CouponDiscount>
                          {couponData.duration && (
                            <CouponDuration>
                              {couponData.duration === 'forever' ? 'Forever' :
                               couponData.duration === 'once' ? 'One time' :
                               couponData.duration === 'repeating' && couponData.duration_in_months ? 
                               `For ${couponData.duration_in_months} months` : 
                               'Limited time'}
                            </CouponDuration>
                          )}
                        </CouponDetails>
                      </CouponInfo>
                    ))}
                  </CouponsContainer>
                )}
                
                {!isFreePlan && (
                  <SubscriptionDescription>
                    {isCancellation 
                      ? "To re-activate your subscription, click the \"Manage Subscription\" button below."
                      : "To update your payment method or view your billing history, please click the \"Manage Subscription\" button below."
                    }
                  </SubscriptionDescription>
                )}
                
                {!isFreePlan && (
                  <SubscriptionActions>
                    <ManageButton 
                      variant="contained"
                      onClick={() => {
                        window.location.href = `https://billing.stripe.com/p/login/4gw5mge0yaWL7fy144?prefilled_email=${encodeURI(email)}`;
                      }}
                    >
                      Manage Subscription
                    </ManageButton>
                    <CancelButton 
                      onClick={handleCancelSubscription}
                      disabled={isCancellation}
                      isCancelled={isCancellation}
                    >
                      {isCancellation ? 'Subscription Cancelled' : 'Cancel Subscription'}
                    </CancelButton>
                  </SubscriptionActions>
                )}
              </SubscriptionCard>
            </SubscriptionSection>
          </Container>
        </MotionContent>
      </PageContainer>
    </>
  );
}

const PageContainer = styled.div`
  background-color: #0C0810;
  height: calc(100dvh - 90px);
  overflow-y: auto;
  padding: 40px 0;
  
  @media only screen and (max-width: 768px) {
    padding: 20px 0;
  }
`;

const MotionContent = styled(motion.div)`
  width: 100%;
  min-height: calc(100vh - 170px);
  
  @media only screen and (max-width: 768px) {
    min-height: calc(100vh - 130px);
  }
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
`;

const PageTitle = styled.h1`
  color: #FDE9FF;
  font-family: Raleway;
  font-size: 48px;
  font-weight: 400;
  margin: 0;
  
  @media only screen and (max-width: 600px) {
    font-size: 32px;
  }
`;


const AccountSection = styled.div`
  margin-bottom: 40px;
`;

const AccountCard = styled.div`
  background: rgba(29, 13, 44, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 32px;
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 64px;
  
  @media only screen and (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const UserDetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserEmail = styled.p`
  color: rgba(245, 225, 255, 0.74);
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  margin: 0;
`;

const UserPlan = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 14px;
  margin: 0;
`;

const CreditsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CreditItem = styled.div<{ color: string }>`
  color: ${props => props.color};
  font-family: Jura;
  font-size: 16px;
  font-weight: 300;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BuyCreditsLink = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-family: Raleway;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
  
  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const SubscriptionSection = styled.div`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const SectionTitle = styled.h2`
  color: #FDE9FF;
  font-family: Raleway;
  font-size: 36px;
  font-weight: 400;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PlansButton = styled(Button)`
  && {
    color: #FDE9FF;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 12px 24px;
    font-family: Raleway;
    text-transform: none;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }
  }
`;

const CreditsButton = styled(Button)`
  && {
    color: #FDE9FF;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 12px 24px;
    font-family: Raleway;
    text-transform: none;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }
  }
`;

const SubscriptionDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-family: Raleway;
  font-size: 16px;
  margin: 24px 0;
  line-height: 1.5;
`;

const SubscriptionCard = styled.div`
  background: rgba(29, 13, 44, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 32px;
`;

const SubscriptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const SubscriptionInfoRow = styled.div<{ isFreePlan?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.isFreePlan ? '0' : '24px'};
  
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const SubscriptionLabel = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-family: Raleway;
  font-size: 14px;
  margin: 0 0 4px 0;
`;

const SubscriptionPlan = styled.h3`
  color: #FDE9FF;
  font-family: Raleway;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SubscriptionActions = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ManageButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6c63ff 0%, #ff74e9 100%);
    color: white;
    border-radius: 12px;
    padding: 12px 24px;
    font-family: Raleway;
    font-size: 16px;
    font-weight: 600;
    text-transform: none;
    width: auto;
    
    &:hover {
      background: linear-gradient(135deg, #5a52cc 0%, #e066d6 100%);
    }
    
    @media only screen and (max-width: 600px) {
      width: 100%;
    }
  }
`;

const CancelButton = styled.button<{ isCancelled?: boolean }>`
  color: ${props => props.isCancelled ? '#666' : '#FFFFFF'};
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-family: Raleway;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.isCancelled ? 'not-allowed' : 'pointer'};
  transition: opacity 0.2s ease;
  opacity: ${props => props.isCancelled ? '0.5' : '1'};
  outline: none;
  
  &:hover:not(:disabled) {
    opacity: ${props => props.isCancelled ? '0.5' : '0.7'};
  }
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  @media only screen and (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

const SubscriptionStatus = styled.div<{ isCancellation: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${props => props.isCancellation ? '#ff6b6b' : '#55CDC4'};
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  
  @media only screen and (max-width: 600px) {
    text-align: center;
    justify-content: center;
  }
`;

const BillingCycle = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
  font-size: 20px;
  padding: 4px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  height: 36px;
`;

const CouponsContainer = styled.div`
  margin: 24px 0;
`;

const CouponInfo = styled.div`
  margin: 16px 0;
  padding: 20px;
  background: rgba(255, 116, 233, 0.05);
  border: 1px solid rgba(255, 116, 233, 0.2);
  border-radius: 12px;
  
  &:first-of-type {
    margin-top: 0;
  }
  
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const CouponLabel = styled.p`
  color: rgba(255, 116, 233, 0.8);
  font-family: Raleway;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CouponDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CouponName = styled.h4`
  color: #FF74E9;
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const CouponDiscount = styled.p`
  color: #FDE9FF;
  font-family: Raleway;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
`;

const CouponDuration = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-family: Raleway;
  font-size: 14px;
  margin: 0;
`;

const PauseStatusContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 24px 0;
  padding: 20px;
  background: rgba(255, 165, 0, 0.05);
  border: 1px solid rgba(255, 165, 0, 0.2);
  border-radius: 12px;
`;

const PauseStatusInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PauseStatusLabel = styled.h4`
  color: #FFA500;
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PauseStatusDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PauseStatusText = styled.p`
  color: #FDE9FF;
  font-family: Raleway;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
`;

const PauseStatusNote = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-family: Raleway;
  font-size: 14px;
  margin: 0;
  font-style: italic;
`;

